import { useState, useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { Bug, RefreshCw, Play, Square, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getLocationDebugState,
  checkLocationPermission,
  invalidatePermissionCache,
  invalidateBackgroundCapabilityCache,
  testBackgroundCapability,
  primeLocationPermissionOnce,
  debugResetPrimeFlag,
  type LocationDebugState,
} from "@/lib/nativeLocation";
import { startJobTracking, stopJobTracking } from "@/lib/trackingBridge";

/**
 * LocationDebugPanel — Debug-only panel for verifying iOS location tracking.
 * 
 * Only renders when TM_DEBUG=true in localStorage.
 * Shows: permission state, cache info, watcher status, BG capability.
 * Actions: invalidate caches, re-prime, emit tracking events, reset prime flag.
 */
export const LocationDebugPanel = () => {
  const [state, setState] = useState<LocationDebugState | null>(null);
  const [bgCapable, setBgCapable] = useState<boolean | null>(null);
  const [testJobId, setTestJobId] = useState("test-job-debug-001");
  const [expanded, setExpanded] = useState(false);

  const isDebug = typeof window !== "undefined" && localStorage.getItem("TM_DEBUG") === "true";
  const isNative = Capacitor.isNativePlatform();

  const refresh = useCallback(async () => {
    const s = getLocationDebugState();
    setState(s);
    if (isNative) {
      const bg = await testBackgroundCapability();
      setBgCapable(bg);
    }
  }, [isNative]);

  useEffect(() => {
    if (!isDebug) return;
    refresh();
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [isDebug, refresh]);

  if (!isDebug) return null;

  const handleInvalidateCaches = () => {
    invalidatePermissionCache();
    invalidateBackgroundCapabilityCache();
    console.log("[LocationDebug] Caches invalidated");
    refresh();
  };

  const handlePrime = async () => {
    console.log("[LocationDebug] Manual prime triggered");
    await primeLocationPermissionOnce();
    refresh();
  };

  const handleResetPrime = () => {
    debugResetPrimeFlag();
    refresh();
  };

  const handleFreshCheck = async () => {
    invalidatePermissionCache();
    const info = await checkLocationPermission(true);
    console.log("[LocationDebug] Fresh check result:", info);
    refresh();
  };

  const handleTestBg = async () => {
    invalidateBackgroundCapabilityCache();
    const result = await testBackgroundCapability(true);
    console.log("[LocationDebug] BG capability test:", result);
    setBgCapable(result);
  };

  const handleStartTracking = () => {
    console.log("[LocationDebug] Emitting START_TRACKING for:", testJobId);
    startJobTracking(testJobId);
  };

  const handleStopTracking = () => {
    console.log("[LocationDebug] Emitting STOP_TRACKING for:", testJobId);
    stopJobTracking(testJobId);
  };

  const handleClearDismiss = () => {
    const keys = Object.keys(localStorage).filter(k => k.startsWith("loc_banner_dismissed_"));
    keys.forEach(k => localStorage.removeItem(k));
    console.log("[LocationDebug] Cleared", keys.length, "banner dismiss keys");
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9999] max-w-sm">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setExpanded(!expanded)}
        className="mb-1 bg-background/95 backdrop-blur border-warning text-warning"
      >
        <Bug className="w-3 h-3 mr-1" />
        Location Debug
      </Button>

      {expanded && (
        <div className="bg-background/95 backdrop-blur border border-border rounded-lg p-3 text-xs space-y-2 max-h-[60vh] overflow-y-auto shadow-xl">
          <h4 className="font-bold text-foreground text-sm">Location Debug State</h4>

          {state && (
            <div className="space-y-1 font-mono text-muted-foreground">
              <div>platform: <span className="text-foreground">{state.platform}</span></div>
              <div>permission: <span className={state.permission === "granted" ? "text-green-500" : "text-warning"}>{state.permission}</span></div>
              <div>cacheAge: <span className="text-foreground">{state.cacheAge !== null ? `${state.cacheAge}ms` : "none"}</span></div>
              <div>primedThisSession: <span className={state.didPrimeThisSession ? "text-green-500" : "text-warning"}>{String(state.didPrimeThisSession)}</span></div>
              <div>bgWatcherId: <span className={state.bgWatcherId ? "text-green-500" : "text-muted-foreground"}>{state.bgWatcherId ?? "null"}</span></div>
              <div>bgCapable: <span className={bgCapable ? "text-green-500" : "text-warning"}>{String(bgCapable)}</span></div>
              <div>bgCapCacheAge: <span className="text-foreground">{state.bgCapabilityCacheAge !== null ? `${state.bgCapabilityCacheAge}ms` : "none"}</span></div>
            </div>
          )}

          <div className="border-t border-border pt-2 space-y-1">
            <h5 className="font-semibold text-foreground">Actions</h5>
            <div className="flex flex-wrap gap-1">
              <Button size="sm" variant="outline" onClick={handleFreshCheck} className="text-xs h-7">
                <RefreshCw className="w-3 h-3 mr-1" />Check Perm
              </Button>
              <Button size="sm" variant="outline" onClick={handleInvalidateCaches} className="text-xs h-7">
                <Trash2 className="w-3 h-3 mr-1" />Clear Cache
              </Button>
              <Button size="sm" variant="outline" onClick={handlePrime} className="text-xs h-7">
                Prime Once
              </Button>
              <Button size="sm" variant="outline" onClick={handleResetPrime} className="text-xs h-7">
                Reset Prime
              </Button>
              <Button size="sm" variant="outline" onClick={handleTestBg} className="text-xs h-7">
                Test BG
              </Button>
              <Button size="sm" variant="outline" onClick={handleClearDismiss} className="text-xs h-7">
                Clear Dismiss
              </Button>
            </div>
          </div>

          <div className="border-t border-border pt-2 space-y-1">
            <h5 className="font-semibold text-foreground">Tracking Events</h5>
            <input
              type="text"
              value={testJobId}
              onChange={(e) => setTestJobId(e.target.value)}
              className="w-full px-2 py-1 border border-border rounded text-xs bg-background text-foreground"
              placeholder="jobId"
            />
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={handleStartTracking} className="text-xs h-7 flex-1">
                <Play className="w-3 h-3 mr-1" />START
              </Button>
              <Button size="sm" variant="outline" onClick={handleStopTracking} className="text-xs h-7 flex-1">
                <Square className="w-3 h-3 mr-1" />STOP
              </Button>
            </div>
          </div>

          <div className="border-t border-border pt-2">
            <h5 className="font-semibold text-foreground mb-1">Structured Log Keys</h5>
            <div className="font-mono text-[10px] text-muted-foreground space-y-0.5">
              <div>LOCATION_PERMISSION_PRIME_START / PRIME_DONE</div>
              <div>LOCATION_STATUS_CHECK / RESULT</div>
              <div>LOCATION_BACKGROUND_CAPABLE</div>
              <div>LOCATION_BANNER_SHOWN / DISMISSED / SETTINGS_OPENED</div>
              <div>WATCHER_STARTED / WATCHER_STOPPED</div>
              <div>TrackingBridge: START_TRACKING / STOP_TRACKING</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
